import {compact} from '@benchling/js-utils';

import isArchived from 'archive/isArchived';
import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {getTagSchemaForStructuredTable} from 'notebook/integrations/registry/StructuredTableUtilsForRegistration';
import {hasUnsubmittedValues} from 'notebook/notes/plugins/fill-in-text-box/FillInTextBoxUtils';
import type {DocumentEditorLock} from 'notebook/realtime/reduceDocumentEditorLockState';
import {
  ProcessTaskStatusStructuredTableColumn,
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {areRowValuesEquivalent} from 'notebook/structured-tables/util/addRowsToTable';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import tableHasUnsubmittedChanges from 'notebook/structured-tables/util/tableHasUnsubmittedChanges';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {hasLoadingCells} from 'notebook/tables/excess/Util';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {isNoteFillInTextBox, isStructuredTable} from 'notebook/TypePredicates';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import {DefaultStatusDisplayName} from 'processes/ProcessConstants';
import {isTerminalStatus} from 'processes/status/TaskStatusUtil';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import EntityStore from 'stores/EntityStore';
// @ts-expect-error untyped JS module
import EntryStore from 'stores/EntryStore';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';

export enum LegacyReviewDisableReason {
  NONE = 'NONE',
  DOCUMENT_NOT_LOADED = 'DOCUMENT_NOT_LOADED',
  UNSUBMITTED_TABLES = 'UNSUBMITTED_TABLES',
  TABLE_CELLS_LOADING = 'TABLE_CELLS_LOADING',
  UNSUBMITTED_TEXT_BOXES = 'UNSUBMITTED_TEXT_BOXES',
  UNSAVED_CHANGES = 'UNSAVED_CHANGES',
  LOCAL_SYNC_CHECKOUT = 'LOCAL_SYNC_CHECKOUT',
  EDITOR_LOCK_SAME_USER = 'EDITOR_LOCK_SAME_USER',
  EDITOR_LOCK_OTHER_USER = 'EDITOR_LOCK_OTHER_USER',
  REVIEW_CRITERIA_LOADING = 'REVIEW_CRITERIA_LOADING',
}

const LEGACY_REVIEW_DISABLE_TOOLTIPS: Record<
  Exclude<
    LegacyReviewDisableReason,
    LegacyReviewDisableReason.NONE | LegacyReviewDisableReason.UNSUBMITTED_TABLES
  >,
  string
> = {
  [LegacyReviewDisableReason.DOCUMENT_NOT_LOADED]: 'Document not yet loaded',
  [LegacyReviewDisableReason.TABLE_CELLS_LOADING]:
    "Please wait for all tables' cells to load before initiating a review",
  [LegacyReviewDisableReason.UNSUBMITTED_TEXT_BOXES]:
    'Please submit all text boxes before initiating a review',
  [LegacyReviewDisableReason.UNSAVED_CHANGES]: 'Entry has unsaved changes',
  [LegacyReviewDisableReason.LOCAL_SYNC_CHECKOUT]:
    'Cannot proceed if a file is checked out with the desktop app. Check in your files to continue.',
  [LegacyReviewDisableReason.EDITOR_LOCK_SAME_USER]:
    'Cannot send for review while you are editing this entry in another window.',
  [LegacyReviewDisableReason.EDITOR_LOCK_OTHER_USER]:
    'Cannot send for review while another user is working on this entry.',
  [LegacyReviewDisableReason.REVIEW_CRITERIA_LOADING]: 'Please wait for review criteria to load.',
};

export interface LegacyReviewActionContext {
  note: EntryNote | undefined;
  hasLocalSyncFiles: boolean;
  hasUnsavedChanges: boolean;
  isLockedToOtherClient: boolean;
  reviewCriteriaGroupsLoading: boolean;
  documentEditorLock?: DocumentEditorLock;
}

export interface LegacyReviewActionState {
  disableReason: LegacyReviewDisableReason;
  tooltipTitle: string | null;
  isBlocked: boolean;
  disableAuthorActions: boolean;
}

function resolveLegacyReviewNoteValidationReason(note: EntryNote | undefined): LegacyReviewDisableReason {
  if (note === undefined) {
    return LegacyReviewDisableReason.DOCUMENT_NOT_LOADED;
  }
  const computedTables = note.noteRuntimeSnapshot.evaluationSnapshot;
  if (computedTables != null && BenchlingConfig.ELN_SEND_REVIEW_REQUIRES_SUBMITTED_TABLES) {
    switch (checkUnsubmittedDataErrors(note)) {
      case UnsubmittedDataError.UNSUBMITTED_TABLES:
        return LegacyReviewDisableReason.UNSUBMITTED_TABLES;
      case UnsubmittedDataError.TABLE_CELLS_LOADING:
        return LegacyReviewDisableReason.TABLE_CELLS_LOADING;
      case UnsubmittedDataError.UNSUBMITTED_TEXT_BOXES:
        return LegacyReviewDisableReason.UNSUBMITTED_TEXT_BOXES;
      default:
        break;
    }
  }
  return LegacyReviewDisableReason.NONE;
}

function getLegacyReviewDisableTooltip(
  disableReason: LegacyReviewDisableReason,
  note: EntryNote | undefined
): string | null {
  if (disableReason === LegacyReviewDisableReason.NONE) {
    return null;
  }
  if (disableReason === LegacyReviewDisableReason.UNSUBMITTED_TABLES) {
    const names = getUnsubmittedTableNames(note);
    return `Please submit all tables before initiating a review:\n${names.join(', ')}`;
  }
  return LEGACY_REVIEW_DISABLE_TOOLTIPS[disableReason];
}

export function getLegacyReviewEditorLockDisableReason(
  documentEditorLock?: DocumentEditorLock
): LegacyReviewDisableReason {
  const lockUserId = documentEditorLock?.userId;
  const currentUserId = EntityStore.currentUser()?.id;
  if (lockUserId != null && currentUserId != null && lockUserId === currentUserId) {
    return LegacyReviewDisableReason.EDITOR_LOCK_SAME_USER;
  }
  return LegacyReviewDisableReason.EDITOR_LOCK_OTHER_USER;
}

export function getLegacyReviewEditorLockTooltip(documentEditorLock?: DocumentEditorLock): string {
  return getLegacyReviewDisableTooltip(
    getLegacyReviewEditorLockDisableReason(documentEditorLock),
    undefined
  )!;
}

/**
 * Returns the highest-priority disable reason for legacy author review actions.
 * Order is intentional: actionable validation errors precede transient loading states.
 */
function resolveLegacyReviewDisableReason({
  note,
  hasLocalSyncFiles,
  hasUnsavedChanges,
  isLockedToOtherClient,
  reviewCriteriaGroupsLoading,
  documentEditorLock,
}: LegacyReviewActionContext): LegacyReviewDisableReason {
  const noteValidationReason = resolveLegacyReviewNoteValidationReason(note);
  if (noteValidationReason !== LegacyReviewDisableReason.NONE) {
    return noteValidationReason;
  }
  if (hasUnsavedChanges) {
    return LegacyReviewDisableReason.UNSAVED_CHANGES;
  }
  if (hasLocalSyncFiles) {
    return LegacyReviewDisableReason.LOCAL_SYNC_CHECKOUT;
  }
  if (isLockedToOtherClient) {
    return getLegacyReviewEditorLockDisableReason(documentEditorLock);
  }
  if (reviewCriteriaGroupsLoading) {
    return LegacyReviewDisableReason.REVIEW_CRITERIA_LOADING;
  }
  return LegacyReviewDisableReason.NONE;
}

export function tableUsesArchivedSchema(structuredTable: UnknownStructuredTable): boolean {
  // don't check for unsubmitted changes on registration tables for an archived entity schema
  // (was one of many issues blocking entry review in CUST-3990, see PR#85798 for more details)
  if (structuredTable.kind === StructuredTableKind.REGISTRATION) {
    const schema = getTagSchemaForStructuredTable(structuredTable);
    if (schema && isArchived(schema)) {
      return true;
    }
  }

  return false;
}

/**
 * In some cases the Output Table in an entry should not block the send-for-review action. The table is protected
 * from deletion (since it cannot be re-added), but not all tasks yield outputs. In particular, in the case
 * of failed or cancelled tasks the operator may be blocked from sending for review since the empty table cannot
 * be submitted nor deleted.
 *
 * @param note The EntryNote that contains the task and output tables
 * @returns boolean flag signaling if we should ignore author action checks on the output table
 */
export function shouldIgnoreOutputTable(note: EntryNote): boolean {
  const outputTableNoteParts = note.noteParts.filter(
    ({item}) => item != null && isStructuredTable(item) && item.kind === StructuredTableKind.PROCESS_OUTPUT
  );
  if (outputTableNoteParts.length === 0) {
    // This entry has no output table
    return false;
  }
  assertAndContinue(
    outputTableNoteParts.length === 1,
    'Expected to find only one output table in execution entry.'
  );
  const outputTable = outputTableNoteParts[0].item as ProcessOutputStructuredTable;

  if (
    outputTable.rows.length !== 1 ||
    !areRowValuesEquivalent(outputTable.rows[0].cells, getBlankRow(0, outputTable.columns, null))
  ) {
    // If the output table is not empty we can't ignore the check
    return false;
  }

  // When the output table is empty (only a single default row) we can ignore iff all tasks have a teminal status
  const taskTableNoteParts = note.noteParts.filter(
    ({item}) => item != null && isStructuredTable(item) && item.kind === StructuredTableKind.PROCESS_TASK
  );
  assertAndContinue(
    taskTableNoteParts.length === 1,
    'Expected to find only one task table in execution entry.'
  );
  const taskTable = taskTableNoteParts[0].item as ProcessTaskStructuredTable;

  const statusColumnIndex = taskTable.columns.findIndex(
    (column) => column.columnType === TableColumnType.PROCESS_TASK_STATUS
  );

  const statusLifecycle = (taskTable.columns[statusColumnIndex] as ProcessTaskStatusStructuredTableColumn)
    .frontEndMetadata?.statusLifecycle;

  return taskTable.rows.every((row) => {
    const typedValue = row.cells[statusColumnIndex].typedValue;
    return (
      typedValue != null &&
      statusLifecycle != null &&
      typedValue.type === BaseTypedValueType.TASK_STATUS &&
      (isTerminalStatus(typedValue.id, statusLifecycle) ||
        typedValue.displayName === DefaultStatusDisplayName.COMPLETED_AFTER_REVIEW)
    );
  });
}

function hasUnsubmittedTextBoxChanges(note: EntryNote): boolean {
  return (
    note.noteParts.find(
      ({item}) => item != null && isNoteFillInTextBox(item) && hasUnsubmittedValues(item)
    ) != null
  );
}

export interface StructuredTableSubmissionStatus {
  name: string;
  kind: string;
  provenanceUuid: string;
  submitted: boolean;
}

export function getTablesSubmissionStates(note: EntryNote): Array<StructuredTableSubmissionStatus> {
  const computedTables = note.noteRuntimeSnapshot.evaluationSnapshot;
  if (computedTables == null) {
    return [];
  }

  return compact(
    note.noteParts.map(({item}) => {
      if (item == null) {
        return null;
      }
      if (!isStructuredTable(item)) {
        return null;
      }
      if (item.kind === StructuredTableKind.PROCESS_OUTPUT && shouldIgnoreOutputTable(note)) {
        // Don't check output tables when all tasks have a terminal status
        return null;
      }

      return {
        name: item.name,
        kind: item.kind,
        provenanceUuid: item.provenanceUuid,
        submitted:
          tableUsesArchivedSchema(item) || !tableHasUnsubmittedChanges(item, computedTables[item.name]),
      };
    })
  );
}

export function getUnsubmittedTableNames(note: EntryNote | undefined): Array<string> {
  if (note == null) {
    return [];
  }
  return getTablesSubmissionStates(note)
    .filter((s) => !s.submitted)
    .map((s) => s.name);
}

export function getUnsubmittedTableNamesForEntry(reviewableId: string): Array<string> {
  const note = StageEntryStore.inStore(reviewableId)
    ? StageEntryStore.getNote(reviewableId)
    : EntryStore.inStore(reviewableId)
      ? EntryStore.getNote(reviewableId)
      : undefined;
  return getUnsubmittedTableNames(note);
}

function hasUnsubmittedTableChanges(note: EntryNote): boolean {
  return getTablesSubmissionStates(note).some((status) => !status.submitted);
}

export enum UnsubmittedDataError {
  NONE,
  TABLE_CELLS_LOADING,
  UNSUBMITTED_TABLES,
  UNSUBMITTED_TEXT_BOXES,
}

export function checkUnsubmittedDataErrors(note: EntryNote): UnsubmittedDataError {
  const computedTables = note.noteRuntimeSnapshot.evaluationSnapshot;
  if (hasUnsubmittedTableChanges(note)) {
    return UnsubmittedDataError.UNSUBMITTED_TABLES;
  } else if (BenchlingConfig.ELN_ENABLE_TEXT_BOX_DATA_MODIFICATIONS && hasUnsubmittedTextBoxChanges(note)) {
    return UnsubmittedDataError.UNSUBMITTED_TEXT_BOXES;
  } else if (computedTables != null && hasLoadingCells(computedTables)) {
    return UnsubmittedDataError.TABLE_CELLS_LOADING;
  }
  return UnsubmittedDataError.NONE;
}

/**
 * This function determines whether we should disable review-locking actions.
 * Review-locking actions include send-for-review and author self-approve, which are blocked when
 * the document is not loaded or when there are unsubmitted tables.
 *
 * Note: For action-aware validation (e.g., allowing RETRACT with unsubmitted tables), call sites
 * should check validation precedence explicitly before calling this function. See EntryNoteTitleBadge
 * for an example of action-aware validation.
 *
 * @param note The entry note to check for validation errors. If undefined, returns error indicating
 *             document is not yet loaded.
 */
export function shouldDisableReviewLockingActions(note: EntryNote | undefined): {
  disableAuthorActions: boolean;
  disableAuthorActionsTooltip: string | null;
} {
  const disableReason = resolveLegacyReviewNoteValidationReason(note);
  return {
    disableAuthorActions: disableReason !== LegacyReviewDisableReason.NONE,
    disableAuthorActionsTooltip: getLegacyReviewDisableTooltip(disableReason, note),
  };
}

export function getLegacyReviewActionState(ctx: LegacyReviewActionContext): LegacyReviewActionState {
  const disableReason = resolveLegacyReviewDisableReason(ctx);
  const noteValidationReason = resolveLegacyReviewNoteValidationReason(ctx.note);
  return {
    disableReason,
    tooltipTitle: getLegacyReviewDisableTooltip(disableReason, ctx.note),
    isBlocked: disableReason !== LegacyReviewDisableReason.NONE,
    disableAuthorActions: noteValidationReason !== LegacyReviewDisableReason.NONE,
  };
}
